import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useGetAccountSettings } from "@/tanstack-hooks";
import { PlatformUnits } from "@/generated-api/models/PlatformUnits";

export const MIN_HEIGHT = 130;
export const MAX_HEIGHT = 220;

export const cmToFeetInches = (cm: number) => {
  const inches = cm / 2.54;
  const feet = Math.floor(inches / 12);
  const remainingInches = Math.round(inches % 12);
  return `${feet}'${remainingInches}"`;
};

export default function HeightSelector({
  value,
  onChange,
}: {
  value: number | undefined;
  onChange: (value: number | null) => void;
}) {
  const accountSettingsQuery = useGetAccountSettings();
  const units: PlatformUnits | undefined =
    accountSettingsQuery.data?.preferred_units;

  const isMetric = units !== PlatformUnits.US;

  const handleHeightChange = (sliderValue: number[]) => {
    onChange(sliderValue[0]);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue)) {
      onChange(null);
      return;
    }
    onChange(newValue);
  };

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(event.target.value, 10);
    if (isNaN(newValue) || newValue < MIN_HEIGHT) {
      onChange(MIN_HEIGHT);
      return;
    } else if (newValue > MAX_HEIGHT) {
      onChange(MAX_HEIGHT);
      return;
    }
  };

  return (
    <div className="flex items-center space-x-4 w-full max-w-2xl">
      <Slider
        value={value ? [value] : [MIN_HEIGHT]}
        onValueChange={handleHeightChange}
        max={MAX_HEIGHT}
        min={MIN_HEIGHT}
        step={1}
        className="flex-grow hover:cursor-pointer"
      />
      {isMetric ? (
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Input
            type="number"
            inputMode="numeric"
            value={value ?? ""}
            onChange={handleInputChange}
            onBlur={handleBlur}
            className="w-14 text-center text-base [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <span className="text-gray-400 w-6">cm</span>
        </div>
      ) : (
        <div className="flex items-center space-x-2 flex-shrink-0">
          <span className="min-w-14 text-center">
            {value ? cmToFeetInches(value) : cmToFeetInches(MIN_HEIGHT)}
          </span>
        </div>
      )}
    </div>
  );
}
